import { defineStore, storeToRefs } from 'pinia';
import { computed, watchEffect } from 'vue';
import { BetSlipState, BetSlipType } from '@/interface/enum';
import { useApp } from '@/libraries/composables/useApp';
import { useDeferredAcceptAnyOddsBanner } from '@/libraries/composables/useDeferredAcceptAnyOddsBanner';
import useState from '@/libraries/composables/useState';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export const useBetSlipStore = defineStore('betSlip', () => {
    const [isShowStakePad, setIsShowStakePad] = useState(true);
    const [betSlipType, setBetSlipType] = useState(BetSlipType.None);
    const [betSlipState, setBetSlipState] = useState(BetSlipState.Closed);
    const [defaultStake, setDefaultStake] = useState(0);
    const [lastBetSlipType, setLastBetSlipType] = useState(BetSlipType.None);
    const [lastBetSlipState, setLastBetSlipState] = useState(BetSlipState.Closed);

    const isShowBetBuilderBetSlip = computed(() => (betSlipState.value === BetSlipState.Mini
            || betSlipState.value === BetSlipState.Medium
            || betSlipState.value === BetSlipState.Big) && betSlipType.value === BetSlipType.BetBuilder);
    const isShowMediumBetBuilderBetSlip = computed(() => betSlipState.value === BetSlipState.Medium && betSlipType.value === BetSlipType.BetBuilder);

    const isShowParlayBetSlip = computed(() => (betSlipState.value === BetSlipState.Mini
        || betSlipState.value === BetSlipState.Medium
        || betSlipState.value === BetSlipState.Big) && betSlipType.value === BetSlipType.Parlay);

    const isShowBigBetSlip = computed(() => betSlipState.value === BetSlipState.Big);
    const isShowMediumBetSlip = computed(() => betSlipState.value === BetSlipState.Medium);

    const { isEuroSite } = storeToRefs(useFeatureToggleStore());
    const isShowMiniBetSlip = computed(() => betSlipState.value === BetSlipState.Mini && !isEuroSite.value);

    const isShowBetSlip = computed(() => betSlipState.value !== BetSlipState.Closed);

    const isShowSingleStateBetSlip = computed(() => betSlipState.value === BetSlipState.Single);

    const isShowSingleBetSlip = computed(() => betSlipType.value === BetSlipType.Single);
    const isShowSingleModeBetSlip = computed(() => isShowBetSlip.value
        && betSlipType.value === BetSlipType.SingleMode);
    const isShowDynamicBetSlip = computed(() => betSlipType.value === BetSlipType.Dynamic);
    const isShowOutrightBetSlip = computed(() => betSlipType.value === BetSlipType.Outright);

    // TCMN-12056: Medium/Big/Single cover enough of the screen (numpad +
    // stake pad) that the in-app PiP FAB should hide; Closed/Mini leave room
    // for it.
    const isBetTicketOpen = computed(() => isShowMediumBetSlip.value || isShowBigBetSlip.value || isShowSingleStateBetSlip.value);

    function showMediumBetSlip() {
        setBetSlipState(BetSlipState.Medium);
    }

    function showMiniBetSlip() {
        setBetSlipState(BetSlipState.Mini);
    }

    function showBigBetSlip() {
        setBetSlipState(BetSlipState.Big);
    }

    function closeBetSlip() {
        setBetSlipType(BetSlipType.None);
        setBetSlipState(BetSlipState.Closed);
    }

    function swapWithLastBetSlip() {
        const previousType = betSlipType.value;
        const previousState = betSlipState.value;
        setBetSlipType(lastBetSlipType.value);
        setBetSlipState(lastBetSlipState.value);
        setLastBetSlipType(previousType);
        setLastBetSlipState(previousState);
    }

    watchEffect(() => window.dispatchEvent(new CustomEvent('sports.betSlip.state', { detail: betSlipState.value })));
    useDeferredAcceptAnyOddsBanner(betSlipState);

    const { setBetTicketOpenInApp } = useApp();
    watchEffect(() => setBetTicketOpenInApp(isBetTicketOpen.value));

    return {
        isShowStakePad,
        betSlipType,
        betSlipState,
        defaultStake,
        isShowBetSlip,
        isShowBetBuilderBetSlip,
        isShowBigBetSlip,
        isShowMediumBetSlip,
        isShowMiniBetSlip,
        isShowParlayBetSlip,
        isShowMediumBetBuilderBetSlip,
        isShowSingleStateBetSlip,
        isShowSingleBetSlip,
        isShowSingleModeBetSlip,
        isShowDynamicBetSlip,
        isShowOutrightBetSlip,
        lastBetSlipType,
        lastBetSlipState,
        setIsShowStakePad,
        setBetSlipType,
        setBetSlipState,
        setDefaultStake,
        showMediumBetSlip,
        showMiniBetSlip,
        showBigBetSlip,
        closeBetSlip,
        setLastBetSlipType,
        setLastBetSlipState,
        swapWithLastBetSlip,
    };
});
